import { FC } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DailyNavClosedTransactionGroup } from 'api';
import { formatProviderCode } from 'utilities';
import { ClosedTransactionsDialogProps } from './ClosedTransactionsDialog.props';

const groupCaption = (group: DailyNavClosedTransactionGroup): string => {
  const providers = group.provider_codes.map(formatProviderCode).join(', ');
  const tradeLabel = group.transaction_count === 1 ? '1 trade' : `${group.transaction_count} trades`;
  return [providers, tradeLabel].filter(Boolean).join(' · ');
};

const ClosedTransactionsDialog: FC<ClosedTransactionsDialogProps> = ({ row, open, onClose }) => {
  const groups = row?.record.secondary_breakdown?.closed_transactions ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      id="closed-transactions-dialog"
      data-testid="closed-transactions-dialog">
      <DialogTitle>
        <Typography variant="h6">
          Closed Transactions{row?.companyName ? ` — ${row.companyName}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Up to the 10 most recent groups. Trades after the last valuation are highlighted.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {groups.length === 0 && (
          <Alert severity="info">No closed secondary transactions are available for this row.</Alert>
        )}
        {groups.length > 0 && (
          <Table size="small" data-testid="closed-transactions-table">
            <TableHead>
              <TableRow>
                <TableCell>Transaction</TableCell>
                <TableCell>Share Class</TableCell>
                <TableCell>Sources</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group, index) => {
                const highlightSx = {
                  color: group.since_last_valuation ? 'error.main' : 'inherit',
                  fontWeight: group.since_last_valuation ? 700 : 400,
                };
                return (
                  <TableRow
                    key={`${group.transaction_date}-${group.price_per_share}-${group.security_type ?? ''}-${index}`}
                    data-since-last-valuation={group.since_last_valuation ? 'true' : undefined}>
                    <TableCell>
                      <Typography variant="body2" sx={highlightSx}>
                        {group.display}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-share-class-${index}`}
                        sx={highlightSx}>
                        {group.security_type || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {groupCaption(group)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} id="closed-transactions-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosedTransactionsDialog;
